import React, { useEffect, useState } from "react";
import { Newspaper, CalendarDays, Radio } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/shared/PageHero";
import { PageCta } from "@/components/shared/PageCta";
import { Section } from "@/components/ui-kit/Kit";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { fetchNews, fetchEvents } from "@/lib/api";

const Empty = ({ icon: Icon, text, testid }) => (
  <div className="mt-10 flex items-center gap-4 border border-dashed border-navy/20 bg-slate-50/60 p-8" data-testid={testid}>
    <Icon className="h-6 w-6 shrink-0 text-navy/25" strokeWidth={1.3} />
    <p className="text-[13px] text-slate-500">{text}</p>
  </div>
);

export const News = () => {
  const { t } = useI18n();
  const p = t("pages.news");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  useSEO({ title: p.title, description: p.lead, path: "/news" });

  useEffect(() => {
    let alive = true;
    fetchNews(24)
      .then((d) => alive && setItems(d.items || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const shown = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid="news-page">
        <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-8">
          {(p.filters || []).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              data-testid={`news-filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-200 ${
                filter === f ? "border-navy bg-navy text-white" : "border-navy/15 text-navy/60 hover:border-navy/40"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <Empty icon={Newspaper} text={p.empty} testid="news-empty" />
        ) : (
          <Stagger className="mt-10 grid gap-px border border-navy/10 bg-navy/10 md:grid-cols-3">
            {shown.map((a, i) => (
              <StaggerItem key={a.slug || i} className="bg-white">
                <article className="h-full p-8" data-testid={`news-item-${i + 1}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-grn">{a.category}</p>
                  <h2 className="mt-5 font-display text-xl font-semibold leading-snug text-navy">{a.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-navy/40">
                    {a.published_at?.slice(0, 10)} {a.author ? `· ${a.author}` : ""}
                  </p>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </Section>
      <PageCta />
    </>
  );
};

export const Events = () => {
  const { t } = useI18n();
  const p = t("pages.events");
  const [items, setItems] = useState([]);
  useSEO({ title: p.title, description: p.lead, path: "/events" });

  useEffect(() => {
    let alive = true;
    fetchEvents()
      .then((d) => alive && setItems(d.items || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid="events-page">
        <div className="flex flex-wrap gap-x-8 gap-y-3 border-b border-navy/10 pb-8">
          {(p.types || []).map((ty) => (
            <span key={ty} className="font-mono text-[10px] uppercase tracking-[0.18em] text-navy/45">
              {ty}
            </span>
          ))}
        </div>
        {items.length === 0 ? (
          <Empty icon={CalendarDays} text={p.empty} testid="events-empty" />
        ) : (
          <Stagger className="mt-10 divide-y divide-navy/10 border-y border-navy/10">
            {items.map((e, i) => (
              <StaggerItem key={e.slug || i}>
                <div className="grid gap-4 py-7 sm:grid-cols-[160px_1fr]" data-testid={`event-${i + 1}`}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-grn">
                    {e.starts_at?.slice(0, 10)}
                  </p>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-navy">{e.title}</h2>
                    <p className="mt-2 text-sm text-slate-600">{e.description}</p>
                    {e.venue && <p className="mt-2 text-[12px] text-slate-500">{e.venue}</p>}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </Section>
      <PageCta />
    </>
  );
};

export const Media = () => {
  const { t } = useI18n();
  const p = t("pages.media");
  useSEO({ title: p.title, description: p.lead, path: "/media" });

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid="media-page">
        <Stagger className="grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2">
          {(p.sections || []).map((s, i) => (
            <StaggerItem key={s} className="bg-white">
              <div className="p-8" data-testid={`media-section-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <Radio className="h-6 w-6 text-navy/30" strokeWidth={1.4} />
                  <span className="font-mono text-[11px] tracking-[0.2em] text-navy/20">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h2 className="mt-8 font-display text-lg font-semibold text-navy">{s}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">{t("common.noContentYet")}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal>
          <p className="mt-10 max-w-3xl border-l-2 border-gold pl-5 text-[12px] leading-relaxed text-slate-500">{p.note}</p>
        </Reveal>
      </Section>
      <PageCta />
    </>
  );
};
